import { inject, Injectable } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { EMPTY, Observable } from 'rxjs';
import { catchError, filter, first, switchMap, take } from 'rxjs/operators';

import { AuthorizationService } from '@bo-client/core/authorization/authorization.service';
import {
  AppNewsModalComponent,
  AppNewsModalData
} from '@bo-client/shared/app-news/app-news-modal/app-news-modal.component';
import {
  APP_NEWS_MENU_SIZE,
  APP_NEWS_STARTUP_SIZE,
  AppNewsModel
} from '@bo-client/shared/app-news/app-news.model';
import { AppNewsService } from '@bo-client/shared/app-news/app-news.service';
import { MatDialogSize } from '@bo-client/shared/constants/mat-dialog-size';
import { UserPreferenceModel } from '@bo-client/shared/models/userBackOfficeSettings';
import { DataShareService } from '@bo-client/shared/services/data-share.service';
import { UserConfigService } from '@bo-client/shared/services/user-config.service';

const APP_NEWS_DIALOG_ID = 'app-news';

/**
 * Zeigt Sachbearbeitern die Neuigkeiten – automatisch beim App-Start und auf Wunsch über das
 * Benutzermenü. Ablauf und Speicherzeitpunkt beschreibt `docs/technical/components/app-news.md`.
 */
@Injectable({ providedIn: 'root' })
export class AppNewsViewerService {
  private appNewsService = inject(AppNewsService);
  private authorizationService = inject(AuthorizationService);
  private dataShareService = inject(DataShareService);
  private userConfigService = inject(UserConfigService);
  private matDialog = inject(MatDialog);

  /** Nutzer, für den die Startprüfung schon lief; die Shell kann mehrfach entstehen. */
  private checkedUserId: number | string | null = null;

  /**
   * Startprüfung, einmal je angemeldetem Nutzer: Gibt es eine Neuigkeit, die nach dem letzten
   * Schließen des Modals veröffentlicht wurde, wird die neueste automatisch angezeigt. Nutzer ohne
   * `ROLE_EDITOR_BASE` lösen keinen Request aus.
   */
  checkOnStartup(): void {
    this.dataShareService.userData$
      .pipe(
        first((user) => !!user),
        filter((user) => this.claimStartupCheck(user.id)),
        filter(() => this.authorizationService.hasRole('ROLE_EDITOR_BASE')),
        switchMap(() => this.appNewsService.getLatestPublishedAt(true)),
        filter((latest) => AppNewsViewerService.isUnseen(latest.latestPublishedAt, this.lastSeenNewsAt())),
        switchMap(() => this.appNewsService.getPublished(APP_NEWS_STARTUP_SIZE, true)),
        filter((news) => news.length > 0),
        catchError((): Observable<AppNewsModel[]> => EMPTY)
      )
      .subscribe((news) => this.open({ size: APP_NEWS_STARTUP_SIZE, news }));
  }

  /** Öffnet die letzten {@link APP_NEWS_MENU_SIZE} Neuigkeiten (Benutzermenü). */
  openFromMenu(): void {
    this.open({ size: APP_NEWS_MENU_SIZE });
  }

  /**
   * `true`, wenn die zuletzt veröffentlichte Neuigkeit neuer ist als der Zeitpunkt, an dem der
   * Nutzer das Modal zuletzt geschlossen hat – oder er es noch nie geschlossen hat.
   */
  static isUnseen(latestPublishedAt: string | null, lastSeenNewsAt: string | undefined): boolean {
    if (!latestPublishedAt) {
      return false;
    }
    const lastSeen = lastSeenNewsAt ? Date.parse(lastSeenNewsAt) : NaN;
    return isNaN(lastSeen) || Date.parse(latestPublishedAt) > lastSeen;
  }

  private open(data: AppNewsModalData): void {
    if (this.matDialog.getDialogById(APP_NEWS_DIALOG_ID)) {
      return;
    }
    const dialogConfig = new MatDialogConfig<AppNewsModalData>();
    dialogConfig.id = APP_NEWS_DIALOG_ID;
    dialogConfig.width = MatDialogSize.mediumWidth;
    dialogConfig.maxWidth = '95vw';
    dialogConfig.maxHeight = MatDialogSize.maxHeight;
    dialogConfig.autoFocus = false;
    dialogConfig.data = data;
    this.matDialog
      .open(AppNewsModalComponent, dialogConfig)
      .afterClosed()
      .pipe(take(1))
      .subscribe(() => this.markSeen());
  }

  /** Erst beim Schließen des Modals – nie beim Seitenaufruf – wird „zuletzt gesehen" gespeichert. */
  private markSeen(): void {
    const userPreferences =
      this.userConfigService.userSettings.getSnapshot()?.userPreferences ?? new UserPreferenceModel();
    this.userConfigService.userSettings.update({
      userPreferences: { ...userPreferences, lastSeenNewsAt: new Date().toISOString() }
    });
  }

  /** `true` beim ersten Aufruf je Nutzer; merkt sich den Nutzer für weitere Aufrufe. */
  private claimStartupCheck(userId: number | string): boolean {
    if (userId === this.checkedUserId) {
      return false;
    }
    this.checkedUserId = userId;
    return true;
  }

  private lastSeenNewsAt(): string | undefined {
    return this.userConfigService.userSettings.getSnapshot()?.userPreferences?.lastSeenNewsAt;
  }
}
